import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import BrowserOnly from 'components/consumer/BrowserOnly';
import RatioContainer from 'components/consumer/RatioContainer';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import JBImage from 'components/essentials/JBImage';
import PDPDesktopFullScreenGallery2 from 'components/consumer/PDP/PDPDesktopFullScreenGallery2';
import PDPGalleryLabelsAndViewingCount from 'components/consumer/PDP/PDPGalleryLabelsAndViewingCount';
import PDPDecideLaterOverlay from 'components/consumer/PDP/PDPDecideLaterOverlay';
import PDPCylindo3dViewer from 'components/consumer/PDP/PDPCylindo3dViewer';
import Placeholder from 'components/consumer/Placeholder';
import PDPFavoriteButton from 'components/consumer/PDPFavoriteButton';
import { markLuxPageNavigationEnd } from 'commons/lux';
import useScreen from 'hooks/useScreen';
import getImageUrlWithoutQuery from 'commons/getImageUrlWithoutQuery';
import BarbieLogo from 'assets/images/made-for-barbie-logo.png';
import BipocLogo from 'assets/images/bipoc-badge.png';
import useIsBrowser from 'hooks/useIsBrowser';
import useBrokenImageUrls from 'global-state/brokenImageUrls/useBrokenImageUrls';
import PDPDesktopThumbnails from './PDPDesktopThumbnails';
import { getMainImageImgixPropsForPhoto } from './helpers/imgix-props';
import usePDPDesktopHeroSizeProps from './helpers/usePDPDesktopHeroSizeProps';
import PDPProductIntroVideo from './PDPProductIntroVideo';
import { PDP_MAIN_IMAGE } from './specs/testIds';
import LinkOrFragment from '../../LinkOrFragment';
import PDPDimensionShotViewer from '../PDPDimensionShotViewer';

const QUICKVIEW_GALLERY_TABLET_IMAGE_WIDTH = '100%';
const QUICKVIEW_GALLERY_MAX_IMAGE_WIDTH = 570;
const dazeyLogo =
  'https://joybird2.imgix.net/user-uploads-staging/JBxDaniDazey-Logo-Button.png';

const PDPDesktopHeroGalleryUI = ({
  campaign,
  configurationPhotos = [],
  cylindoFeatures,
  dimensionsImage,
  enableCrops = true,
  introVideoIds,
  isConfigFlipped = false,
  isCylindoAvailable,
  isCylindoConfigurationPhotos,
  isEditOrder = true,
  isVideoSelected,
  loading = false,
  mainPhotoLink,
  mode = 'pdp',
  pdpIsModularBYOProduct = false,
  productName,
  selectedPhoto,
  setIsVideoSelected,
  setSelectedPhoto,
  showDecideLaterOverlay,
  studioPhotos = [],
  viewGalleryImageEvent,
  dimensionShotUrls = [],
  currentDimensionShot,
  hasMultipleDimensionShots = false,
  selectedDimensionShotIndex = 0,
  goToNextDimensionShot,
  goToPreviousDimensionShot,
  shouldShowDimensionThumbnail = false,
}) => {
  const { isMobile, isMobileOrTablet, isDesktop } = useScreen();

  const isQuickviewMode = mode === 'quickview';
  const isPdpMode = mode === 'pdp';

  const isBrowser = useIsBrowser();
  const [isFullScreenGalleryOpen, setIsFullScreenGalleryOpen] = useState(false);

  const brokenImageUrls = useBrokenImageUrls();

  const isStudioGallery = selectedPhoto?.type === 'studio';
  const isDimensionImg = selectedPhoto?.type === 'dimensional';
  const isConfigGallery =
    selectedPhoto?.type === 'configuration' ||
    selectedPhoto?.type === 'cylindo';
  const isBuildYourOwnImg = selectedPhoto?.type === 'buildyourown';

  const photos = useMemo(() => {
    const allPhotos = isStudioGallery
      ? studioPhotos
      : pdpIsModularBYOProduct && isBuildYourOwnImg
      ? [selectedPhoto]
      : configurationPhotos;

    const validPhotos = allPhotos.filter(({ src }) => {
      const cleanImageUrl = getImageUrlWithoutQuery(src);
      // Remove any broken images from the array passed to the gallery modal
      return !brokenImageUrls.includes(cleanImageUrl);
    });

    return validPhotos;
  }, [
    brokenImageUrls,
    configurationPhotos,
    pdpIsModularBYOProduct,
    isBuildYourOwnImg,
    isStudioGallery,
    selectedPhoto,
    studioPhotos,
  ]);

  const selectedPhotoIndex = Math.max(
    0,
    photos.findIndex(({ src }) => src === selectedPhoto?.src)
  );

  const mainPhotoSizeProps = usePDPDesktopHeroSizeProps();

  const introVideoId =
    introVideoIds && (isMobile ? introVideoIds.mobile : introVideoIds.desktop);

  const ratioForCroppedImages = isMobileOrTablet ? 1 : 2 / 3;
  const ratio = loading || enableCrops ? ratioForCroppedImages : 1 / 2;

  const spaceAround =
    32 /* Photo top padding */ +
    72 /* Photo bottom padding & thumbnails */ +
    90; /* Key selling points */

  const maxGalleryWidth = useMemo(() => {
    if (isQuickviewMode) {
      return QUICKVIEW_GALLERY_MAX_IMAGE_WIDTH;
    }

    return `calc((100vh - ${spaceAround}px) / ${ratio})`;
  }, [isQuickviewMode, ratio, spaceAround]);

  const galleryWidth = useMemo(() => {
    if (isQuickviewMode && !isDesktop) {
      return QUICKVIEW_GALLERY_TABLET_IMAGE_WIDTH;
    }

    if (isQuickviewMode) {
      return QUICKVIEW_GALLERY_MAX_IMAGE_WIDTH;
    }

    return undefined;
  }, [isDesktop, isQuickviewMode]);

  const openGalleryModal = () => {
    setIsFullScreenGalleryOpen(true);
  };

  const closeGalleryModal = () => {
    setIsFullScreenGalleryOpen(false);
  };

  const isMainPhotoLinked = isQuickviewMode && mainPhotoLink;

  return (
    <>
      <div
        className="mb-8 ml-auto mr-auto relative"
        style={{ maxWidth: maxGalleryWidth, width: galleryWidth }}
      >
        <LinkOrFragment
          className={
            isMainPhotoLinked
              ? 'block focus-visible:outline-offset-4'
              : undefined
          }
          to={isMainPhotoLinked ? mainPhotoLink : undefined}
        >
          <RatioContainer
            className={classnames('bg-transparent border-0 focus:outline-0', {
              'PDPDesktopHeroGalleryUI__MainPhoto--mode-pdp': mode === 'pdp',
              'PDPDesktopHeroGalleryUI__MainPhoto--mode-quickview':
                mode === 'quickview',
              'flex justify-center': brokenImageUrls.includes(
                selectedPhoto?.src
              ),
            })}
            ratio={ratio}
            onClick={() => {
              if (
                isPdpMode &&
                // If Cylindo is enabled for config gallery, only open full
                // screen gallery if studio gallery is selected
                ((!isCylindoConfigurationPhotos && isConfigGallery) ||
                  isStudioGallery) &&
                !isVideoSelected
              ) {
                openGalleryModal();
              }
            }}
          >
            {!loading &&
              // render Image only for studio/dimension images in "Cylindo Variant"
              (isCylindoAvailable && isCylindoConfigurationPhotos
                ? isStudioGallery || isDimensionImg
                : true) &&
              !isVideoSelected &&
              !!selectedPhoto &&
              (!!selectedPhoto.src || isDimensionImg) &&
              (isDimensionImg ? (
                <PDPDimensionShotViewer
                  currentDimensionShot={currentDimensionShot}
                  hasMultipleDimensionShots={hasMultipleDimensionShots}
                  selectedDimensionShotIndex={selectedDimensionShotIndex}
                  totalDimensionShots={dimensionShotUrls.length}
                  onPrevious={goToPreviousDimensionShot}
                  onNext={goToNextDimensionShot}
                  className="h-full w-full"
                  {...getMainImageImgixPropsForPhoto(
                    selectedPhoto,
                    enableCrops
                  )}
                  {...mainPhotoSizeProps}
                  data-testid={PDP_MAIN_IMAGE}
                  lazy={false}
                  priority
                  onLoad={markLuxPageNavigationEnd}
                />
              ) : (
                <JBProductImage
                  className={classnames(
                    'h-full w-full object-contain object-center select-none',
                    {
                      'p-8': !isStudioGallery && !enableCrops,
                      'scale-x-[-1]':
                        !isStudioGallery && isConfigFlipped && !isDimensionImg,
                      'hover:cursor-[unset]':
                        !isStudioGallery && isCylindoConfigurationPhotos,
                      'hover:cursor-zoom-in': mode === 'pdp',
                    }
                  )}
                  {...getMainImageImgixPropsForPhoto(
                    selectedPhoto,
                    enableCrops
                  )}
                  {...mainPhotoSizeProps}
                  data-testid={PDP_MAIN_IMAGE}
                  lazy={false}
                  priority
                  onLoad={markLuxPageNavigationEnd}
                />
              ))}

            {!loading && isVideoSelected && introVideoId && (
              <PDPProductIntroVideo videoId={introVideoId} />
            )}

            {!loading && isCylindoAvailable && (
              <div
                className={classnames(
                  'w-full h-full [&_.mxt-btn-download]:hidden [&_.mxt-btn-expand]:hidden',
                  {
                    invisible:
                      !(isCylindoAvailable && isCylindoConfigurationPhotos) ||
                      isStudioGallery ||
                      isDimensionImg ||
                      isVideoSelected,
                  }
                )}
              >
                <PDPCylindo3dViewer
                  cylindoFeatures={cylindoFeatures}
                  rotateIndex={isConfigGallery ? selectedPhotoIndex : -1}
                />
              </div>
            )}

            {!loading &&
              showDecideLaterOverlay &&
              !(isStudioGallery || isVideoSelected) && (
                <PDPDecideLaterOverlay />
              )}

            {loading && <Placeholder type="rect" loading />}
          </RatioContainer>
        </LinkOrFragment>

        {!loading &&
        pdpIsModularBYOProduct &&
        !isStudioGallery &&
        !isBuildYourOwnImg ? (
          <div className="text-sm font-bold bg-[rgba(44,44,44,.7)] text-white left-[calc(50%_-_85px)] opacity-60 absolute text-center top-[40%] w-[190px] z-[2]">
            Sample configuration shown
          </div>
        ) : null}

        {campaign === 'barbie' && (
          <img
            src={BarbieLogo}
            alt="barbie-logo"
            className="w-20 h-20 absolute top-12 left-4 z-50"
          />
        )}

        {campaign === 'bipoc' && (
          <img
            src={BipocLogo}
            alt="bipoc-owned-logo"
            className="w-20 h-20 absolute top-12 left-4 z-50"
          />
        )}

        {campaign === 'dazey' && (
          <JBImage
            src={dazeyLogo}
            className="absolute top-10 left-2 z-50"
            width={120}
          />
        )}

        <PDPDesktopThumbnails
          isEditOrder={isEditOrder}
          configurationPhotos={configurationPhotos}
          dimensionsImage={dimensionsImage}
          shouldShowDimensionThumbnail={shouldShowDimensionThumbnail}
          dimensionShotUrls={dimensionShotUrls}
          enableCrops={enableCrops}
          pdpIsModularBYOProduct={pdpIsModularBYOProduct}
          introVideoId={introVideoId}
          isConfigFlipped={isConfigFlipped}
          isCylindoConfigurationPhotos={isCylindoConfigurationPhotos}
          isVideoSelected={isVideoSelected}
          loading={loading || !isBrowser}
          mode={mode}
          openModal={openGalleryModal}
          productName={productName}
          selectedPhoto={selectedPhoto}
          setSelectedPhoto={setSelectedPhoto}
          setIsVideoSelected={setIsVideoSelected}
          studioPhotos={studioPhotos}
          viewGalleryImageEvent={viewGalleryImageEvent}
        />

        {!loading && (
          <div className="flex justify-between left-4 absolute right-4 top-4 z-[2]">
            <PDPGalleryLabelsAndViewingCount mode={mode} />
            {!isQuickviewMode && <PDPFavoriteButton />}
          </div>
        )}
      </div>
      <BrowserOnly>
        <PDPDesktopFullScreenGallery2
          isEditOrder={isEditOrder}
          isOpen={isFullScreenGalleryOpen}
          photos={photos}
          initialSelectedPhotoIndex={selectedPhotoIndex}
          onClose={closeGalleryModal}
          isStudioGallery={isStudioGallery}
          pdpIsModularBYOProduct={pdpIsModularBYOProduct}
          productName={productName}
          key={(productName || '').split(' ').join('-')}
          setSelectedPhoto={setSelectedPhoto}
          introVideoId={introVideoId}
          isVideoSelected={isVideoSelected}
          setIsVideoSelected={setIsVideoSelected}
        />
      </BrowserOnly>
    </>
  );
};

PDPDesktopHeroGalleryUI.propTypes = {
  configurationPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      src: PropTypes.string.isRequired,
      type: PropTypes.oneOf([
        'buildyourown',
        'configuration',
        'cylindo',
        'dimensional',
        'studio',
      ]),
    })
  ),
  dimensionsImage: PropTypes.shape({
    id: PropTypes.number.isRequired,
    src: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }),
  isEditOrder: PropTypes.bool,
  enableCrops: PropTypes.bool,
  loading: PropTypes.bool,
  selectedPhoto: PropTypes.shape({
    type: PropTypes.oneOf([
      'buildyourown',
      'configuration',
      'cylindo',
      'dimensional',
      'studio',
    ]),
    src: PropTypes.string,
  }),
  setSelectedPhoto: PropTypes.func,
  showDecideLaterOverlay: PropTypes.bool,
  studioPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      src: PropTypes.string.isRequired,
    })
  ),
  isConfigFlipped: PropTypes.bool,
  introVideoIds: PropTypes.shape({
    desktop: PropTypes.string,
    mobile: PropTypes.string,
  }),
  setIsVideoSelected: PropTypes.func,
  isVideoSelected: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  productName: PropTypes.string,
  isCylindoAvailable: PropTypes.bool,
  cylindoFeatures: PropTypes.arrayOf(PropTypes.string),
  isCylindoConfigurationPhotos: PropTypes.bool,
  viewGalleryImageEvent: PropTypes.func,
  mode: PropTypes.oneOf(['pdp', 'quickview']),
  mainPhotoLink: PropTypes.string,
  campaign: PropTypes.string,
};

export default PDPDesktopHeroGalleryUI;
